import { useEffect, useRef, useState } from 'react'
import { canAnimateReveal } from '@/lib/fx'

// Flips to true once the ref'd element enters the viewport, triggering the scroll-reveal transition.
// Content is never left hidden: phones, slow connections and reduced-motion visitors get it straight away,
// elements the observer is slow to report are revealed by a scroll check, and a timer is the last resort.
export function useRevealed(threshold = 0.01) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    if (typeof IntersectionObserver === 'undefined' || !canAnimateReveal()) {
      setVisible(true)
      return undefined
    }

    let done = false
    let frame = 0
    const cleanups = []
    const reveal = () => {
      if (done) return
      done = true
      setVisible(true)
      cleanups.forEach((fn) => fn())
    }

    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && reveal(), { threshold, rootMargin: '100px 0px 15% 0px' })
    observer.observe(node)
    cleanups.push(() => observer.disconnect())

    const sweep = () => {
      frame = 0
      if (node.getBoundingClientRect().top < window.innerHeight * 1.1) reveal()
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(sweep)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    cleanups.push(() => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
    })

    // Last resort: once scrolling has stopped, anything at or above the viewport gets revealed.
    let idle = 0
    const onIdle = () => {
      clearTimeout(idle)
      idle = setTimeout(() => node.getBoundingClientRect().top < window.innerHeight * 1.5 && reveal(), 350)
    }
    window.addEventListener('scroll', onIdle, { passive: true })
    cleanups.push(() => {
      window.removeEventListener('scroll', onIdle)
      clearTimeout(idle)
    })

    return () => {
      done = true
      cleanups.forEach((fn) => fn())
    }
  }, [threshold])

  return [ref, visible]
}
